import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Eye, LogOut, Search, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import RegionSelector from "@/components/RegionSelector";

// Primary links stay in the main bar; secondary links collapse into "Explore"
const PRIMARY = [
  { to: "/videos", label: "Videos" },
  { to: "/audiobooks", label: "Audiobooks" },
  { to: "/healing", label: "Healing" },
  { to: "/classes", label: "Classes" },
  { to: "/store", label: "Store" },
];

const MORE = [
  { to: "/library", label: "Downloads" },
  { to: "/network", label: "Collaboration / Network" },
  { to: "/forum", label: "Community Forum" },
];

const ALL_LINKS = [{ to: "/", label: "Home" }, ...PRIMARY, ...MORE];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const moreRef = useRef(null);

  const handleLogout = async () => { await logout(); nav("/"); };

  const runSearch = (e) => {
    e.preventDefault();
    nav(q.trim() ? `/videos?search=${encodeURIComponent(q.trim())}` : "/videos");
    setSearchOpen(false);
    setOpen(false);
    setQ("");
  };

  // close "Explore" dropdown on outside click / route change
  useEffect(() => {
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  useEffect(() => { setMoreOpen(false); }, [loc.pathname]);

  const moreActive = MORE.some((l) => l.to === loc.pathname);
  const linkCls = (active) =>
    `text-sm tracking-wide whitespace-nowrap transition-colors hover:text-gold ${active ? "text-gold" : "text-bone/70"}`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="nav-logo">
          <Eye className="text-gold group-hover:rotate-12 transition-transform" strokeWidth={1.5} size={26} />
          <span className="font-serif text-2xl tracking-tight text-bone">Apocalypz<span className="text-gold">.org</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {PRIMARY.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={linkCls(loc.pathname === l.to)}>
              {l.label}
            </Link>
          ))}

          {/* Explore dropdown for secondary sections */}
          <div className="relative" ref={moreRef}>
            <button onClick={() => setMoreOpen((s) => !s)} data-testid="nav-explore-toggle"
              className={`flex items-center gap-1 ${linkCls(moreActive || moreOpen)}`}>
              Explore <ChevronDown size={15} strokeWidth={2} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 top-full mt-3 w-60 glass border border-gold/20 shadow-2xl shadow-black/50 overflow-hidden origin-top-right animate-explore-in" data-testid="nav-explore-menu">
                {MORE.map((l) => {
                  const active = loc.pathname === l.to;
                  return (
                    <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                      className={`group/link relative flex items-center px-5 py-3.5 text-sm border-b border-white/5 last:border-0 transition-colors hover:bg-gold/10 hover:text-gold ${active ? "text-gold bg-gold/5" : "text-bone/80"}`}>
                      <span className={`absolute left-0 top-0 bottom-0 w-0.5 bg-gold transition-transform duration-200 origin-top ${active ? "scale-y-100" : "scale-y-0 group-hover/link:scale-y-100"}`} />
                      {l.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <div className="w-px h-5 bg-white/15" />

          <button onClick={() => setSearchOpen((s) => !s)} data-testid="nav-search-toggle" aria-label="Search"
            className={`transition-colors hover:text-gold ${searchOpen ? "text-gold" : "text-bone/70"}`}>
            <Search size={18} strokeWidth={1.75} />
          </button>
          <RegionSelector />
          {user ? (
            <div className="flex items-center gap-4">
              <Link to="/admin" data-testid="nav-admin" className="text-sm text-gold border border-gold/40 px-4 py-1.5 hover:bg-gold hover:text-black transition-colors whitespace-nowrap">Admin</Link>
              <button onClick={handleLogout} data-testid="nav-logout" className="text-bone/60 hover:text-gold"><LogOut size={18} strokeWidth={1.5} /></button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="text-sm text-black bg-gold px-5 py-1.5 hover:bg-gold2 transition-colors font-medium whitespace-nowrap">Login</Link>
          )}
        </nav>

        <div className="lg:hidden flex items-center gap-4">
          <button onClick={() => setSearchOpen((s) => !s)} data-testid="nav-search-toggle-mobile" aria-label="Search" className="text-bone hover:text-gold">
            <Search size={24} strokeWidth={1.75} />
          </button>
          <button className="text-bone" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="glass border-t border-white/10 px-5 md:px-8 py-4" data-testid="nav-search-bar">
          <form onSubmit={runSearch} className="max-w-7xl mx-auto flex items-stretch bg-black/40 border border-white/15 focus-within:border-gold/60 transition-colors">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teachers & topics across the site..."
              data-testid="nav-search-input"
              className="flex-1 bg-transparent px-4 py-3 text-bone placeholder:text-bone/40 focus:outline-none" />
            <button type="submit" data-testid="nav-search-submit" aria-label="Submit search"
              className="px-5 bg-white/5 hover:bg-gold hover:text-black text-gold transition-colors flex items-center justify-center">
              <Search size={18} />
            </button>
          </form>
        </div>
      )}

      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-4" data-testid="mobile-menu">
          {ALL_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className={`text-base ${loc.pathname === l.to ? "text-gold" : "text-bone/80"}`}>{l.label}</Link>
          ))}
          <div className="pt-1"><RegionSelector align="left" /></div>
          {user ? (
            <>
              <Link to="/admin" onClick={() => setOpen(false)} className="text-gold">Admin Panel</Link>
              <button onClick={handleLogout} className="text-left text-bone/70">Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="text-gold">Credentialed Login</Link>
          )}
        </div>
      )}
    </header>
  );
}
